import { PrismicRichText } from '@prismicio/react';
import IconArrowDown from '@remote-com/norma/icons/arrow-down.svg';
import Image from 'next/image';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import { fluidProp } from 'remote-com-shared/style/helpers';
import styled, { css } from 'styled-components';

import BookPurpleIcon from './assets/book-purple.svg';
import OnboadingBgIllustration from './assets/onboarding-illustration.svg';

import { fieldHasContent, transformProperties } from '@/src/domains/prismic/helpers';
import { tabletBreakpoint } from '@/src/domains/shared/breakpoints';
import { ScrollButton } from '@/src/views/G2LeaderPage/HeroSlice';

export const Section = styled.section`
  background: #f8f5ff;
  ${fluidProp(['padding-bottom', 'padding-top'], '80px', '110px')}
  font-family: var(--typography-fontBrand);
  position: relative;
  overflow: hidden;

  ${ScrollButton} {
    color: var(--colors-irisBlue);
    font-weight: 500;

    svg {
      padding-left: 5px;
      width: 18px;
    }
  }

  ${ContentContainer} {
    z-index: 1;
    position: relative;
  }
`;

export const Title = styled.div`
  color: var(--colors-darkBlue);
  ${({ theme }) => theme.typography.titleSM}
  margin-bottom: 31px;
  font-family: var(--typography-fontBossa);
`;

export const Description = styled.div`
  ${({ theme }) => theme.typography.bodyLead}
  margin-bottom: 32px;
  color: ${({ theme }) => theme.colors.darkBlue_75};
  max-width: 90%;

  strong {
    color: var(--colors-darkBlue);
  }

  a {
    color: var(--colors-irisBlue);
    text-decoration: underline;

    &:hover {
      text-decoration-thickness: 2px;
    }
  }
`;

export const Fields = styled.div`
  display: flex;
  gap: 60px;
  flex-wrap: wrap;
`;

export const Field = styled.div`
  width: 40%;
  display: flex;

  ${Description} {
    p {
      margin-top: 0;
    }
  }

  ${tabletBreakpoint(css`
    width: 100%;
  `)};
`;

export const ImageWrapper = styled.div`
  min-width: 24px;
  margin-right: 20px;
`;

export const Note = styled(Description)`
  && {
    color: var(--colors-darkBlue);
  }
`;

export const OnboadingBgWrapper = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;

  svg {
    vertical-align: bottom;
  }
`;

export function OnboardingSlice({ slice }) {
  const { primary, fields } = transformProperties(slice);
  const { title, description, note, readMore } = primary;

  return (
    <Section id="onboarding">
      <ContentContainer>
        <BookPurpleIcon />
        {fieldHasContent(title) && (
          <Title>
            <PrismicRichText field={title} />
          </Title>
        )}
        {fieldHasContent(description) && (
          <Description>
            <PrismicRichText field={description} />
          </Description>
        )}
        <Fields>
          {fields.map(({ illustration, description: fieldDescription }, index) => (
            <Field key={index}>
              <ImageWrapper>
                <Image
                  src={illustration.url}
                  alt={illustration.alt || ''}
                  width={24}
                  height={24}
                  quality="100"
                />
              </ImageWrapper>
              {fieldHasContent(fieldDescription) && (
                <Description>
                  <PrismicRichText field={fieldDescription} />
                </Description>
              )}
            </Field>
          ))}
        </Fields>
        {fieldHasContent(note) && <Note>{note}</Note>}
        <ScrollButton href="#comparison-table">
          {readMore || 'Read more'} <IconArrowDown />
        </ScrollButton>
      </ContentContainer>
      <OnboadingBgWrapper>
        <OnboadingBgIllustration />
      </OnboadingBgWrapper>
    </Section>
  );
}
